import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { optimizeImage } from "@/lib/mediaUrls";

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

/**
 * Full-screen overlay slide viewer (no Fullscreen API dependency — works
 * in iPhone Safari). Renders the live active slide from props, so host
 * advances appear instantly. Pinch / double-tap zoom + pan.
 */
export const ExpandedSlideViewer = ({ slide, index, total, onClose, canHost, onPrev, onNext, pending }) => {
  const [zoom, setZoom] = useState({ s: 1, x: 0, y: 0 });
  const gesture = useRef(null);
  const lastTap = useRef(0);

  useEffect(() => { setZoom({ s: 1, x: 0, y: 0 }); }, [slide?.slide_id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prevOverflow; window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const onTouchStart = (e) => {
    const t = e.touches;
    if (t.length === 2) gesture.current = { kind: "pinch", d: dist(t), s: zoom.s };
    else if (t.length === 1) {
      const now = Date.now();
      if (now - lastTap.current < 300) {
        setZoom((z) => (z.s > 1 ? { s: 1, x: 0, y: 0 } : { s: 2.5, x: 0, y: 0 }));
        lastTap.current = 0;
        return;
      }
      lastTap.current = now;
      gesture.current = { kind: "pan", sx: t[0].clientX, sy: t[0].clientY, x: zoom.x, y: zoom.y };
    }
  };
  const onTouchMove = (e) => {
    const g = gesture.current;
    if (!g) return;
    const t = e.touches;
    if (g.kind === "pinch" && t.length === 2) {
      setZoom((z) => ({ ...z, s: clamp((g.s * dist(t)) / g.d, 1, 4) }));
    } else if (g.kind === "pan" && t.length === 1 && zoom.s > 1) {
      setZoom((z) => ({ ...z, x: g.x + (t[0].clientX - g.sx), y: g.y + (t[0].clientY - g.sy) }));
    }
  };
  const onTouchEnd = () => {
    gesture.current = null;
    setZoom((z) => (z.s <= 1.02 ? { s: 1, x: 0, y: 0 } : z));
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Expanded presentation"
      data-testid="workshop-expanded-viewer"
    >
      <div className="flex items-center justify-between px-3 py-2 text-white/80 text-xs"
        style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}>
        <span data-testid="workshop-expanded-progress">{total > 0 && index >= 0 ? `Slide ${index + 1} of ${total}` : "Waiting for the host…"}</span>
        <span className="hidden portrait:inline text-[11px] text-white/50" data-testid="workshop-rotate-hint">Rotate phone or pinch to zoom</span>
        <button type="button" onClick={onClose}
          className="px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs font-bold"
          data-testid="workshop-expanded-close">
          ✕ Close
        </button>
      </div>
      <div
        className="relative flex-1 overflow-hidden"
        style={{ touchAction: "none" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onDoubleClick={() => setZoom((z) => (z.s > 1 ? { s: 1, x: 0, y: 0 } : { s: 2, x: 0, y: 0 }))}
      >
        {slide ? (
          <img
            key={slide.slide_id}
            src={optimizeImage(slide.image_url, 'cover')}
            alt={slide.caption || "Workshop slide"}
            draggable={false}
            className="absolute inset-0 w-full h-full object-contain select-none"
            style={{ transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.s})`, transition: gesture.current ? "none" : "transform 150ms ease-out" }}
            data-testid="workshop-expanded-slide"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-purple-200/70 text-sm">Waiting for the next slide…</div>
        )}
      </div>
      {canHost && (
        <div className="flex items-center justify-center gap-3 py-2"
          style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}>
          <button type="button" onClick={onPrev} disabled={pending || total === 0}
            className="text-sm font-bold px-4 py-2 rounded-full bg-white/10 text-white disabled:opacity-40"
            data-testid="workshop-expanded-prev">← Previous</button>
          <button type="button" onClick={onNext} disabled={pending || total === 0}
            className="text-sm font-bold px-4 py-2 rounded-full bg-white/10 text-white disabled:opacity-40"
            data-testid="workshop-expanded-next">Next →</button>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default ExpandedSlideViewer;
